import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { supabase, toAppError } from '@/services/supabase';
import { useAuthStore } from './auth';

// Tipos vêm de @assessor/shared: a mesma definição usada pelo guard puro
// e, futuramente, pelo worker. Redeclarar aqui abriria espaço para drift.
import type {
  EffectiveStatus,
  Entitlement,
  SubscriptionStatus,
  WorkspaceRole,
} from '@assessor/shared';

export type { EffectiveStatus, Entitlement, SubscriptionStatus, WorkspaceRole };

type Role = WorkspaceRole;
type Effective = EffectiveStatus;

export interface WorkspaceSummary {
  id: string;
  name: string;
  role: Role;
  currency: string;
  timezone: string;
}

export interface WorkspaceAccess {
  workspace_id: string;
  role: Role;
  subscription: {
    id: string;
    status: SubscriptionStatus;
    effective_status: Effective;
    trial_ends_at: string | null;
    grace_until: string | null;
    started_at: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    canceled_at: string | null;
    checkout_url: string | null;
  } | null;
  plan: {
    id: string;
    code: string;
    name: string;
    price: number;
    currency: string;
    interval: string;
    max_seats: number;
  } | null;
  entitlements: Record<string, Entitlement>;
  usage: Record<string, number>;
  is_platform_admin: boolean;
}

const ROLE_RANK: Record<Role, number> = { VIEWER: 1, MEMBER: 2, ADMIN: 3, OWNER: 4 };

/**
 * Workspace ativo e tudo que decorre dele: papel, assinatura, plano,
 * entitlements e uso.
 *
 * Todo o estado vem de UMA chamada a `get_workspace_access` — o Subscription
 * Guard precisa desses dados juntos e consistentes, e resolver isso com
 * cinco queries paralelas produziria estados intermediários incoerentes
 * (plano já carregado, uso ainda não) que a UI leria como "sem acesso".
 */
export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref<WorkspaceSummary[]>([]);
  const currentId = ref<string | null>(null);
  const access = ref<WorkspaceAccess | null>(null);
  const loading = ref(false);
  const loaded = ref(false);

  const current = computed(() => workspaces.value.find((w) => w.id === currentId.value) ?? null);
  const role = computed<Role | null>(() => access.value?.role ?? null);
  const subscription = computed(() => access.value?.subscription ?? null);
  const plan = computed(() => access.value?.plan ?? null);
  const isPlatformAdmin = computed(() => access.value?.is_platform_admin ?? false);

  /** Estado que a UI mostra. Diferente do status cru: GRACE ainda opera. */
  const effectiveStatus = computed<Effective>(
    () => access.value?.subscription?.effective_status ?? 'NONE',
  );

  /** A assinatura permite operar agora? */
  const isSubscriptionActive = computed(() =>
    ['TRIAL', 'ACTIVE', 'GRACE'].includes(effectiveStatus.value),
  );

  function hasRole(min: Role): boolean {
    if (!role.value) return false;
    return ROLE_RANK[role.value] >= ROLE_RANK[min];
  }

  async function loadWorkspaces(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.user) return;

    const { data, error } = await supabase
      .from('workspace_members')
      .select('role, workspaces!inner(id, name, currency, timezone)')
      .eq('user_id', auth.user.id)
      .eq('status', 'ACTIVE');

    if (error) throw toAppError(error, 'Não foi possível carregar seus workspaces.');

    workspaces.value = (data ?? []).map((row) => {
      const ws = row.workspaces;
      return {
        id: ws.id,
        name: ws.name,
        role: row.role,
        currency: ws.currency,
        timezone: ws.timezone,
      };
    });
  }

  async function loadAccess(workspaceId: string): Promise<void> {
    const { data, error } = await supabase.rpc('get_workspace_access', {
      p_workspace_id: workspaceId,
    });
    if (error) throw toAppError(error, 'Não foi possível verificar seu acesso.');
    access.value = data as unknown as WorkspaceAccess;
  }

  async function select(workspaceId: string): Promise<void> {
    currentId.value = workspaceId;
    localStorage.setItem('assessor:workspace', workspaceId);
    await loadAccess(workspaceId);
  }

  /** Carrega workspaces e seleciona o ativo. Idempotente. */
  async function initialize(): Promise<void> {
    if (loading.value) return;
    loading.value = true;
    try {
      await loadWorkspaces();
      if (workspaces.value.length === 0) {
        access.value = null;
        currentId.value = null;
        loaded.value = true;
        return;
      }

      const auth = useAuthStore();
      const stored = localStorage.getItem('assessor:workspace');
      const preferred =
        [stored, auth.profile?.default_workspace_id].find(
          (id) => id && workspaces.value.some((w) => w.id === id),
        ) ?? workspaces.value[0]?.id;

      if (preferred) await select(preferred);
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  /** Recarrega o acesso — usado após checkout e mudanças de plano. */
  async function refresh(): Promise<void> {
    if (currentId.value) await loadAccess(currentId.value);
  }

  function reset(): void {
    workspaces.value = [];
    currentId.value = null;
    access.value = null;
    loaded.value = false;
  }

  return {
    workspaces,
    currentId,
    current,
    access,
    role,
    plan,
    subscription,
    loading,
    loaded,
    effectiveStatus,
    isSubscriptionActive,
    isPlatformAdmin,
    hasRole,
    initialize,
    select,
    refresh,
    reset,
  };
});
